import { useMemo } from "react"

import type { GroupedCitations } from "../types/active-citations"
import type { CheckedState } from "@radix-ui/react-checkbox"

import { useNavigatorStore } from "@/stores/navigator-store"

export function useCitationLibrary(
  citationsGroupedByLibrary: GroupedCitations,
  sessionType: string
) {
  const navigatorStore = useNavigatorStore(sessionType)
  const activeCitations = navigatorStore((state) => state.activeCitations)
  const toggleActiveCitationsInLibrary = navigatorStore(
    (state) => state.toggleActiveCitationsInLibrary
  )
  const activeCitationsSet = useMemo(() => new Set(activeCitations), [activeCitations])

  function getLibraryCounts(library: string) {
    const libraryCitations = citationsGroupedByLibrary.get(library) ?? []
    const active = libraryCitations.filter((citation) =>
      activeCitationsSet.has(citation.fullName)
    ).length

    return { active, total: libraryCitations.length }
  }

  function getLibraryCheckedState(library: string): CheckedState {
    const { active, total } = getLibraryCounts(library)
    if (total === 0) return false
    if (active === total) return true
    if (active > 0) return "indeterminate"
    return false
  }

  function toggleAllActiveCitationsInLibrary(library: string, checked: CheckedState) {
    if (checked === "indeterminate") return
    const libraryCitations = citationsGroupedByLibrary.get(library)

    if (!libraryCitations) return

    const allCitationsInLibrary = libraryCitations.map((citation) => citation.fullName)

    toggleActiveCitationsInLibrary(allCitationsInLibrary, checked)
  }

  return {
    getLibraryCheckedState,
    getLibraryCounts,
    toggleAllActiveCitationsInLibrary,
  }
}
